import React, { useEffect, useState } from 'react';
import { Opportunity as Lead } from '../types';
import SidebarPanel from './ui/SidebarPanel';

interface LeadFormSidebarProps {
  isOpen: boolean;
  lead?: Lead | null;
  onSave: (data: Omit<Lead, 'id' | 'createdDate' | 'lastModifiedDate'>) => void;
  onClose: () => void;
}

const dealStageOptions = [
  'Qualification',
  'Discovery / Demo Scheduled',
  'Proposal Sent',
  'Negotiation',
  'Closed Won',
  'Closed Lost',
  'On Hold',
];

const companySizeOptions = [
  '1-5',
  '6-20',
  '21-100',
  '100-500',
  '500+',
];

const dealTypeOptions = [
  'New Business',
  'Existing Business',
  'Renewal',
  'Upsell',
  'Cross-sell',
];

const dealSizeOptions = [
  '1-5,000',
  '5,000-10,000',
  '10,000-15,000',
  '15,000-25,000',
  '25,000-50,000',
  '50,000+' 
];

const currencyOptions = [
  'USD',
  'EUR',
  'JPY',
  'GBP',
  'AUD',
];

interface LeadFormData {
  companyName: string;
  contactName: string;
  companySize: string;
  dealStage: string;
  expectedDealSize: string;
  currency: string;
  targetGoLiveDate: string;
  dealType: string;
  salesRep: string;
  soc2: string;
  salesRegionTerritory: string;
  notes: string;
}

const LeadFormSidebar: React.FC<LeadFormSidebarProps> = ({ isOpen, lead, onSave, onClose }) => {
  const [formData, setFormData] = useState<LeadFormData>({
    companyName: '',
    contactName: '',
    companySize: '',
    dealStage: '',
    expectedDealSize: '',
    currency: '',
    targetGoLiveDate: '',
    dealType: '',
    salesRep: '',
    soc2: '',
    salesRegionTerritory: '',
    notes: '',
  });

  useEffect(() => {
    if (lead) {
      const { id, createdDate, lastModifiedDate, ...editableData } = lead;
      setFormData({
        companyName: editableData.companyName || '',
        contactName: editableData.contactName || '',
        companySize: editableData.companySize,
        dealStage: editableData.dealStage,
        expectedDealSize: editableData.expectedDealSize,
        currency: editableData.currency || '',
        targetGoLiveDate: editableData.targetGoLiveDate || '',
        dealType: editableData.dealType,
        salesRep: editableData.salesRep,
        soc2: String(editableData.soc2),
        salesRegionTerritory: editableData.salesRegionTerritory || '',
        notes: editableData.notes || '',
      });
    } else {
      // Reset form when creating new lead
      setFormData({
        companyName: '',
        contactName: '',
        companySize: '',
        dealStage: '',
        expectedDealSize: '',
        currency: '',
        targetGoLiveDate: '',
        dealType: '',
        salesRep: '',
        soc2: '',
        salesRegionTerritory: '',
        notes: '',
      });
    }
  }, [lead, isOpen]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const submissionData: Omit<Lead, 'id' | 'createdDate' | 'lastModifiedDate'> = {
      ...formData,
      soc2: formData.soc2 === 'true',
    };

    // Prepare lead data (excluding notes)
    const { notes, ...leadData } = submissionData;
    
    // Download the lead JSON immediately (as before)
    const blob = new Blob([JSON.stringify(leadData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `lead.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    // ALSO store in localStorage for potential combination with meeting data later
    localStorage.setItem('pendingLeadData', JSON.stringify(leadData));
    onSave(submissionData);
  };

  const renderInput = (label: string, name: keyof typeof formData, type: string = 'text', required: boolean = false, options?: string[]) => (
    <div className="mb-4">
      <label htmlFor={name} className="block text-sm font-medium text-gray-700 mb-1">
        {label}{required && <span className='text-red-500'>*</span>}
      </label>
      {type === 'textarea' ? (
        <textarea
          id={name}
          name={name}
          value={formData[name] as string || ''}
          onChange={handleChange}
          rows={3}
          className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-teal-500 focus:border-teal-500"
        />
      ) : type === 'select' ? (
        <select
          id={name}
          name={name}
          value={formData[name] || ''}
          onChange={handleChange}
          required={required}
          className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-teal-500 focus:border-teal-500 bg-white"
        >
          <option value="" disabled>Select...</option>
          {options?.map(option => <option key={option} value={option}>{option}</option>)}
        </select>
      ) : (
        <input
          type={type}
          id={name}
          name={name}
          value={formData[name] as string | number || ''}
          onChange={handleChange}
          required={required}
          className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-teal-500 focus:border-teal-500"
          step={type === 'number' ? 'any' : undefined}
        />
      )}
    </div>
  );

  return (
    <SidebarPanel
      isOpen={isOpen}
      onClose={onClose}
      title={lead ? 'Edit Lead' : 'Create New Lead'}
      width="xl"
      headerColor="bg-teal-500"
      headerTextColor="text-white"
    >
      <div className="p-6 pb-24">
        <form onSubmit={handleSubmit}>
          <div className="space-y-6">
            {/* Basic Information Section */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4 pb-2 border-b border-gray-200">
                Basic Information
              </h3>
              <div className="grid grid-cols-1 gap-4">
                {renderInput('Company Name', 'companyName')}
                {renderInput('Contact Name', 'contactName')}
                {renderInput('Sales Rep [Name]', 'salesRep', 'text', true)}
                {renderInput('City, State (for example: Ann Arbor, Michigan)', 'salesRegionTerritory')}
              </div>
            </div>

            {/* Deal Information Section */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4 pb-2 border-b border-gray-200">
                Deal Information
              </h3>
              <div className="grid grid-cols-1 gap-4">
                {renderInput('Deal Stage', 'dealStage', 'select', true, dealStageOptions)}
                {renderInput('Deal Type', 'dealType', 'select', true, dealTypeOptions)}
                {renderInput('Expected Deal Size', 'expectedDealSize', 'select', true, dealSizeOptions)}
                {renderInput('Currency', 'currency', 'select', false, currencyOptions)}
              </div>
            </div>

            {/* Company Information Section */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4 pb-2 border-b border-gray-200">
                Company Information
              </h3>
              <div className="grid grid-cols-1 gap-4">
                {renderInput('Company Size', 'companySize', 'select', true, companySizeOptions)}
                {renderInput('When Needed Working', 'targetGoLiveDate', 'select', true, [
                  '1-2 weeks',
                  '2 weeks - 1 month',
                  '1 month - 2 months',
                  '2 months - 6 months',
                  '6 months - 1 year',
                ])}
                {renderInput('Need SOC2?', 'soc2', 'select', true, ['true', 'false'])}
              </div>
            </div>

            {/* Additional Notes Section */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4 pb-2 border-b border-gray-200">
                Additional Notes
              </h3>
              {renderInput('Notes', 'notes', 'textarea')}
            </div>
          </div>
          
          {/* Fixed Footer with Action Buttons */}
          <div className="fixed bottom-0 right-0 bg-white border-t border-gray-200 p-4 w-[40rem] flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-teal-600 border border-transparent rounded-md shadow-sm hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
            >
              {lead ? 'Save Changes' : 'Create Lead'}
            </button>
          </div>
        </form>
      </div>
    </SidebarPanel>
  );
};

export default LeadFormSidebar;
